export const MAX_HIGHLIGHTED_MARKETS = 3;

/**
 * Parses the `highlighted_markets` URL query parameter.
 *
 * Format: "ticker1:hex1,ticker2:hex2,..."
 * Hex values are accepted with or without a `#` prefix and normalized to `#RRGGBB`.
 *
 * Returns a ticker → `#RRGGBB` map, preserving param order and capped at
 * `MAX_HIGHLIGHTED_MARKETS` entries.
 * Returns `undefined` when the param is absent, not a string, or all pairs are malformed.
 */
export function parseHighlightedMarketsParam(
    param: string | string[] | null | undefined,
): Record<string, string> | undefined {
    if (!param || typeof param !== 'string') return undefined;
    const result: Record<string, string> = {};
    for (const pair of param.split(',')) {
        const colonIdx = pair.lastIndexOf(':');
        if (colonIdx < 1) continue;
        const ticker = pair.slice(0, colonIdx).trim();
        const rawHex = pair.slice(colonIdx + 1).trim();
        if (!ticker || !rawHex) continue;
        if (!/^#?[0-9A-Fa-f]{6}$/.test(rawHex)) continue;
        result[ticker] = rawHex.startsWith('#') ? rawHex : `#${rawHex}`;
        if (Object.keys(result).length >= MAX_HIGHLIGHTED_MARKETS) break;
    }
    return Object.keys(result).length > 0 ? result : undefined;
}
